import { useMemo, useRef } from 'react';
import useInternalLinks from '@/hooks/useInternalLinks';
import errorLogger from '@/utils/errorLogger';
import { zodResolver } from '@hookform/resolvers/zod';
import { graphql, navigate } from 'gatsby';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../components/FormButton/FormButton';
import FormError from '../components/FormError/FormError';
import FormRow from '../components/FormRow/FormRow';
import Input from '../components/Input/Input';
import { FormFooter } from '../components/FormLayout/FormLayout';
import { StyledForm } from './ContactForm.styles';
import Select from '../components/Select/Select';
import Textarea from '../components/Textarea/Textarea';
import countryList from '../constants/countryList';
import getTranslatedRequestTypeList from '../constants/requestTypeList';
import { getContactFormSchema } from './validation';
import pushInDataLayer from '@/utils/pushInDataLayer';
import GoogleRecaptcha from '../components/GoogleRecaptcha/GoogleRecaptcha';
import { type ReCAPTCHA } from 'react-google-recaptcha';
import { isTokenRecaptchaValid } from '@/utils/validateGoogleRecaptchaToken';
import getCheqHiddenFieldValue from '../components/CheqHiddenField/getCheqHiddenFieldValue';
import CheqHiddenField from '../components/CheqHiddenField/CheqHiddenField';
import LegalText, { type LegalTextProps } from '../components/LegalText/LegalText';
import MarketingOptOut from '../components/MarketingOptOut/MarketingOptOut';
import useSetGeolocatedCountry from '../hooks/useSetGeolocatedCountry';
import { NEVER, ZodIssueCode } from 'zod';
import useFormComplete from '../hooks/useFormComplete';
import getSalesForceId from '@/utils/getSalesForceId';
import { useLocation } from '@reach/router';
import { isPathMigratedToRR7 } from '@/rr7MigrationPaths';

const ContactForm = ({ contactFormElements: elements, system }: Queries.CONTACT_FORMFragment) => {
  const {
    campaign_id,
    form_submit_title,
    success_redirect_page,
    options,
    legal_text,
    form_type,
    default_request_type,
  } = elements;

  const isRequestTypeFieldRequired =
    options?.value?.includes('set_request_type_field_required') ?? false;

  /**
   * NOTE: We superRefine and transform  here instead of doing it in `validation.ts`
   * because Zod currently has an issue with typing `superRefine`,
   * and it'd prevent us from extending the schema as we do in
   * src/components/Forms/LoginSectionForm/validation.ts
   *
   * This will be fixed with Zod 4.
   * @see
   */
  const validationSchema = useMemo(
    () =>
      getContactFormSchema({ isRequestTypeFieldRequired }).transform(
        ({ number_of_developers, request_type, ...data }, ctx) => {
          if (request_type === 'sales' && !number_of_developers) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: 'form.Required field',
              path: ['number_of_developers'],
            });

            return NEVER;
          }

          return {
            ...data,
            request_type,
            // force the field to undefined if the request_type is not sales. We do not want that field in this case
            number_of_developers: request_type === 'sales' ? number_of_developers : undefined,
          };
        }
      ),
    [isRequestTypeFieldRequired]
  );

  const { t } = useTranslation();
  const getInternalLinkURLByCodename = useInternalLinks();
  const requestTypes = getTranslatedRequestTypeList(t);

  const defaultRequestType =
    typeof default_request_type?.value === 'string'
      ? requestTypes.find((r) => r.value === default_request_type.value!.replaceAll('_', '-'))
      : null;

  const hasDefaultRequestType = defaultRequestType !== null;
  const leadCampaignId = getSalesForceId();
  const { pathname } = useLocation();

  const {
    register,
    control,
    formState: { errors, isSubmitting },
    setError,
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      company: '',
      country: null,
      first_name: '',
      last_name: '',
      email: '',
      message: '',
      phone: '',
      form_type: 'contact',
      request_type: defaultRequestType || null,
      scope: form_type?.value ?? 'contact',
      number_of_developers: null,
      campaign_id: leadCampaignId ?? campaign_id?.value,
    },
  });

  const isFormCompleteReady = useFormComplete({
    setValue,
    selectMappings: {
      country: countryList,
    },
  });
  const enhanceFormComplete = options?.value?.includes('enhance_form_complete') ?? false;
  const shouldHideFields = enhanceFormComplete && !isFormCompleteReady;

  const requestType = watch('request_type');

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });

  const hideLabel = options?.value?.includes('hide_labels') ?? false;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const redirectURL =
    getInternalLinkURLByCodename(success_redirect_page.value?.system?.codename) ?? '/';

  const isRedirectMigrated = isPathMigratedToRR7(redirectURL);

  const onSubmit = handleSubmit(async (formValues) => {
    const token = await recaptchaRef.current?.executeAsync();
    await isTokenRecaptchaValid(token ?? null, 'information-form');

    const cq_req_id = getCheqHiddenFieldValue();

    try {
      const requestBody = {
        ...formValues,
        cq_req_id,
      };

      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'content-type': `application/json`,
        },
      });

      const responseBody = await response.json();

      if (!responseBody.success) {
        throw new Error(responseBody.message);
      }

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'information',
          email: formValues.email,
          country: formValues.country,
          fromPage: pathname,
        },
      });

      /* istanbul ignore if */
      if (isRedirectMigrated) {
        globalThis.location.href = redirectURL;
      } else {
        navigate(redirectURL);
      }
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: t('form.Could not submit form') });
    }
  });

  const emailInput = (
    <Input
      type="email"
      id="email"
      placeholder={t('form.Email')}
      formGroupProps={{ label: 'Email', hideLabel }}
      required
      {...register('email')}
      errors={errors}
      disabled={isSubmitting}
    />
  );

  return (
    <StyledForm onSubmit={onSubmit} noValidate id={system.codename}>
      {enhanceFormComplete && emailInput}
      <FormRow>
        <Input
          type="text"
          id="first_name"
          placeholder={t('form.First Name')}
          formGroupProps={{
            label: 'First Name',
            hideLabel,
            isHidden: shouldHideFields,
          }}
          required
          {...register('first_name')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Input
          type="text"
          id="last_name"
          placeholder={t('form.Last Name')}
          formGroupProps={{ label: 'Last Name', hideLabel, isHidden: shouldHideFields }}
          required
          {...register('last_name')}
          errors={errors}
          disabled={isSubmitting}
        />
      </FormRow>
      {!enhanceFormComplete && emailInput}
      <Input
        type="text"
        id="company"
        placeholder={t('form.Company Name')}
        formGroupProps={{ label: 'Company Name', hideLabel, isHidden: shouldHideFields }}
        required
        {...register('company')}
        errors={errors}
        disabled={isSubmitting}
      />
      <Input
        type="text"
        id="phone"
        placeholder={t('form.Phone Number')}
        formGroupProps={{ label: 'Phone Number', hideLabel, isHidden: shouldHideFields }}
        {...register('phone')}
        errors={errors}
        disabled={isSubmitting}
      />
      <input type="hidden" {...register('form_type')} />
      <input type="hidden" {...register('scope')} />
      <input type="hidden" {...register('campaign_id')} />
      <CheqHiddenField />
      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <Select
            errors={errors}
            required
            id="country"
            placeholder={t('form.Select a country')}
            options={countryList}
            formGroupProps={{ label: 'Country', hideLabel }}
            isDisabled={isSubmitting}
            {...field}
          />
        )}
      />
      {hasDefaultRequestType ? null : (
        <Controller
          name="request_type"
          control={control}
          render={({ field }) => (
            <Select
              errors={errors}
              required={isRequestTypeFieldRequired}
              id="request_type"
              placeholder={t('form.Select the nature of your inquiry')}
              options={requestTypes}
              formGroupProps={{ label: 'Request type', hideLabel }}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
      )}
      {requestType?.value === 'sales' ? (
        <Controller
          control={control}
          name="number_of_developers"
          render={({ field }) => (
            <Select
              errors={errors}
              required
              id="number_of_developers"
              placeholder={'Select # of Developers'}
              options={[
                {
                  label: '< 50 developers',
                  value: '<50',
                },
                {
                  label: '50 - 500 developers',
                  value: '50-500',
                },
                {
                  label: '500+ developers',
                  value: '500+',
                },
              ]}
              formGroupProps={{
                label: '# of Developers in Company',
                hideLabel,
              }}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
      ) : null}
      <Textarea
        id="message"
        formGroupProps={{ label: 'Message', hideLabel }}
        placeholder={t('form.Tell us how we can help')}
        required
        {...register('message')}
        errors={errors}
        disabled={isSubmitting}
      />
      <MarketingOptOut
        label={t('form.Marketing Emails Opt Out')}
        errors={errors}
        register={register}
        disabled={isSubmitting}
      />
      <FormFooter isCondensed>
        <Button className="btn" type="submit" variant="primary" isLoading={isSubmitting}>
          {form_submit_title.value}
        </Button>
        <LegalText richText={legal_text as LegalTextProps['richText']} />
        {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
      </FormFooter>
      <GoogleRecaptcha ref={recaptchaRef} />
    </StyledForm>
  );
};

export default ContactForm;

export const fragment = graphql`
  fragment CONTACT_FORM on kontent_item_contact_form {
    __typename
    system {
      codename
      type
      id
    }
    contactFormElements: elements {
      campaign_id {
        value
      }
      form_type {
        value
      }
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      form_submit_title {
        value
      }
      success_redirect_page {
        value {
          system {
            codename
            id
          }
          id
          elements {
            url_slug {
              value
            }
          }
        }
      }
      options {
        value
      }
      default_request_type {
        value
      }
    }
  }
`;
